import classNames from 'classnames';
import { ReactNode, useCallback, useLayoutEffect, useRef } from 'react';
import { Bubble, BubbleProps } from 'yoso';
import { fixedForwardRef } from '~/components/Util/react';
import styles from './DumbTooltip.module.scss';

export type DumbTooltipProps = Omit<BubbleProps, 'direction'> & {
  active?: boolean;
  className?: string;
  direction?: 'top' | 'bottom';
  tooltipText?: ReactNode;
  children?: ReactNode;
  minOffsetX?: number;
};

export const DumbTooltip = fixedForwardRef<HTMLDivElement, DumbTooltipProps>(
  (
    {
      className,
      children,
      active = false,
      direction = 'top',
      tooltipText,
      minOffsetX = 16,
      ...restProps
    },
    ref
  ) => {
    const bubbleRef = useRef<HTMLDivElement>(null);
    const calcOffset = useCallback(() => {
      if (!bubbleRef.current) return;
      const el = bubbleRef.current;
      const { left, right } = el.getBoundingClientRect();

      if (left < minOffsetX) {
        el.style.setProperty('--tooltip-offset-x', `${-left + minOffsetX}px`);
      } else if (right > document.body.offsetWidth - minOffsetX) {
        el.style.setProperty(
          '--tooltip-offset-x',
          `${document.body.offsetWidth - minOffsetX - right}px`
        );
      }
    }, [minOffsetX, bubbleRef]);

    useLayoutEffect(() => {
      if (active) {
        calcOffset();
      }
    }, [active, calcOffset]);

    return (
      <div
        ref={ref}
        className={classNames(styles.root, className, styles[direction])}
        {...restProps}>
        {children}
        <Bubble
          ref={bubbleRef}
          className={classNames(styles.bubble, { [styles.active]: active })}
          direction={direction}
          {...restProps}>
          {tooltipText}
        </Bubble>
      </div>
    );
  }
);
